/**
 * One Wise credit waiting on a person — `mockups/studio/money.html` §Wise review
 * queue, and the four shapes it draws.
 *
 * The mockup gives each of its four credits a different set of buttons, and
 * `.docs/20-studio-portal-pages.md` §Work breakdown E lists them as a set. **They
 * come from the row, not from a list written here**: a credit with one candidate can
 * be matched to it, a credit with several can be split across them, a credit whose
 * sender resolves to nobody can only be parked. `UnmatchedPaymentSchema.candidates`
 * and `.client` are what decide, so a fifth shape of credit gets the right buttons
 * without a fifth branch.
 *
 * ## The one rule this component exists to keep
 *
 * `payments_match$` refuses a body whose allocations plus `hold_on_account` do not
 * come to the credit — every unit has to go somewhere. That is why matching is a
 * dialog with an editor in it rather than a button that guesses: the amounts are
 * editable, the remainder is printed **as it is being built**, and a body that does
 * not add up cannot be sent because the confirm button is disabled while it does not.
 *
 * This is also the one file on the money page that does arithmetic on a `Money`, and
 * it is arithmetic on an *input* rather than on a figure: `sum` and `subtract` are
 * `packages/shared/src/money.ts`'s, which is where §5 says the only arithmetic lives.
 * Nothing here computes something the read model should have said.
 *
 * ## What is not here
 *
 * The mockup's **"Ask them"** is not a button, because there is no template behind
 * it. `notifications_send`'s sendable list has sixteen entries and not one of them
 * says "which invoice was this transfer for?" — `payment_failed_reminder` is about an
 * invoice that is past due, which a credit in this queue may well have already paid.
 * Sending the wrong mail is worse than not having the button; the session report asks
 * for the template.
 */

import { useState } from 'react'

import {
  formatList,
  subtract,
  sum,
  toMajorUnits,
  isNegative,
  isZero,
  money as makeMoney,
  type Money,
  type UnmatchedPaymentView,
} from '@fareast/shared'
import { z } from 'zod'

import { messageOf } from '../../api/problem.js'
import { reviewOf, useStudioAction, useStudioRead } from '../../api/studio.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow } from '../portal/Button.js'
import { Empty } from '../portal/Disclosure.js'
import { Input, Select, useZodForm } from '../portal/Form.js'
import { Row, Push, Stack } from '../portal/Layout.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { amountOf } from '../portal/schemaForm.js'
import { NeedsBar, NeedsItem, Notice, Tag } from '../portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { B, DateText, DayText, Muted, Num, Prose, Ref, Small } from '../portal/Text.js'
import { NeedsReviewNotice } from './NeedsReviewNotice.js'

type Candidate = UnmatchedPaymentView['candidates'][number]

/**
 * Why a credit is here, in the words the mockup's own chip uses.
 *
 * `MATCH_REASONS` is the API's closed list and this is its rendering; a reason with
 * no entry falls back to its own name rather than to nothing, because a chip that
 * disappeared would take the only explanation of the row with it.
 */
const REASON_LABELS: Readonly<Record<string, string>> = {
  no_reference: 'No reference',
  unknown_reference: 'Unknown reference',
  already_paid: 'Already paid',
  amount_mismatch: 'Amount mismatch',
  fx_converted: 'FX converted',
  partial: 'Partial',
  surplus: 'Surplus',
  ambiguous_amount: 'Ambiguous amount',
  no_client: 'No client',
}

export type ReviewQueueItemProps = {
  readonly payment: UnmatchedPaymentView
  /** The client this looks like, as everybody says it. The slug when nobody knows. */
  readonly clientName?: string | undefined
}

export function ReviewQueueItem({ payment, clientName }: ReviewQueueItemProps) {
  const format = useFormat()
  const [preset, setPreset] = useState<readonly Draft[] | null>(null)
  const [manual, setManual] = useState(false)
  const [parking, setParking] = useState(false)
  const [booking, setBooking] = useState(false)
  const [showCandidates, setShowCandidates] = useState(false)

  /*
   * Read on demand, and only once somebody asks. The row already carries candidates
   * — `UnmatchedPaymentSchema.candidates` — so this is not how the buttons are
   * decided; it is the mockup's "See 3 candidates", which is a *fresh* look at what
   * is still open, because an hour on this screen is an hour in which somebody else
   * may have settled one of them.
   */
  const candidates = useStudioRead(
    'payments_list_candidates',
    { payment: payment.id },
    { enabled: showCandidates, inline: true },
  )

  const live = showCandidates
    ? (candidates.data?.candidates ?? payment.candidates)
    : payment.candidates
  /*
   * Which candidates a **button** may name.
   *
   * `payments_match` allocates in the credit's own currency, so `fill` leaves a
   * candidate in another one blank — and a "Split across …" drawn over two of those
   * opens a dialog with two empty boxes, a disabled confirm and no way forward. The
   * world's $860 credit has exactly that shape: two EUR invoices under a USD credit.
   * They are still listed and still described; what they do not get is a button
   * promising to do something the next screen cannot do.
   */
  const allocatable = live.filter((one) => one.outstanding.currency === payment.amount.currency)
  const reason = payment.reasons[0]

  return (
    <NeedsItem>
      <NeedsBar />
      <Stack gap="tight">
        <Row>
          <B>
            <Num value={payment.amount} />
          </B>
          <Small>
            <Muted>
              {/* `stamp`, not `datetime`: the zone is pinned to UTC and a time of day
                  that does not say which clock it is on is the one thing
                  `format/useFormat.ts` forbids. A Wise credit arriving at 08:55 UTC is
                  04:55 for the person reading it, and neither number is wrong. */}
              <DateText value={payment.received_at} style="stamp" zone="UTC" />
            </Muted>
          </Small>
          <Ref value={stepIdOf(payment.external_id)} />
          <Push />
          {reason === undefined ? null : <Tag>{REASON_LABELS[reason] ?? reason}</Tag>}
        </Row>

        <Small>{sentenceOf(payment, clientName, format.money)}</Small>

        {showCandidates ? (
          <CandidateList
            candidates={live}
            pending={candidates.isPending}
            failed={candidates.isError}
          />
        ) : null}

        <ButtonRow>
          {allocatable.length === 1 && allocatable[0] !== undefined ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setPreset(fill(payment.amount, allocatable.slice(0, 1)))
              }}
            >
              {`Match to ${allocatable[0].invoice_ref}`}
            </Button>
          ) : null}

          {allocatable.length > 1 &&
          allocatable[0] !== undefined &&
          allocatable[1] !== undefined ? (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setPreset(fill(payment.amount, allocatable))
                }}
              >
                {`Split across ${allocatable[0].invoice_ref} and ${allocatable[1].invoice_ref}`}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setPreset(fill(payment.amount, allocatable.slice(0, 1)))
                }}
              >
                {`Match to ${allocatable[0].invoice_ref} only`}
              </Button>
            </>
          ) : null}

          {/*
            **Always offered, and it no longer builds its rows from the shortlist.**

            It was `allocatable.length > 0`, which withheld the only remaining
            operation from exactly the two credits whose own sentence asks for it:
            $1,500 from Shadia, whose client resolves and who has no open invoice
            within fifty dollars, and $860 from PARADIGM, whose two candidates are
            both EUR under a USD credit. Both cards read "…so it needs placing by
            hand" over *Park as unidentified* and nothing else.

            So the dialog reads its own list — `payments_list_candidates` with
            `scope: 'allocatable'`, every open invoice in the credit's currency,
            which is exactly the set `payments_match` accepts — rather than being
            handed the ≤3 the queue guessed at. That is also the right list when the
            shortlist *is* populated: the nearest three are a ranking, not the
            choices.
          */}
          <Button
            size="sm"
            onClick={() => {
              setManual(true)
            }}
          >
            Allocate manually
          </Button>

          {payment.candidates.length > 1 && !showCandidates ? (
            <Button
              size="sm"
              onClick={() => {
                setShowCandidates(true)
              }}
            >
              {`See ${String(payment.candidates.length)} candidates`}
            </Button>
          ) : null}

          {/*
            Session 47: money that is an account's and no invoice's — Far East Novels'
            Patreon payouts. Not offered on money held on account, which is a promise to
            that client's invoices and which `payments_book_income` refuses.
          */}
          {payment.status === 'on_account' ? null : (
            <Button
              size="sm"
              onClick={() => {
                setBooking(true)
              }}
            >
              Book as income
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setParking(true)
            }}
          >
            Park as unidentified
          </Button>
        </ButtonRow>
      </Stack>

      {preset === null ? null : (
        <MatchDialog
          key={preset.map((one) => `${one.candidate.invoice_ref}:${one.major}`).join('|')}
          payment={payment}
          drafts={preset}
          onClose={() => {
            setPreset(null)
          }}
        />
      )}

      {manual ? (
        <ManualDialog
          payment={payment}
          onClose={() => {
            setManual(false)
          }}
        />
      ) : null}

      {parking ? (
        <ParkDialog
          payment={payment}
          onClose={() => {
            setParking(false)
          }}
        />
      ) : null}

      {booking ? (
        <IncomeDialog
          payment={payment}
          onClose={() => {
            setBooking(false)
          }}
        />
      ) : null}
    </NeedsItem>
  )
}

/* --------------------------------------------------------------- the sentence */

/**
 * What this credit is, in the mockup's own voice: the reference verbatim, who sent
 * it, and what it is near.
 *
 * The mockup writes a different sentence for each of its four; this writes one
 * sentence out of the fields all four have, because a per-shape sentence is a
 * per-shape branch and the next credit is a fifth shape.
 */
export function sentenceOf(
  payment: UnmatchedPaymentView,
  clientName: string | undefined,
  money: (value: Money) => string,
): string {
  const parts: string[] = []

  parts.push(
    payment.reference === ''
      ? 'No reference on the transfer.'
      : `Reference: “${payment.reference}”.`,
  )
  if (payment.sender !== '') parts.push(`Sent by ${payment.sender}.`)
  parts.push(
    payment.client === null
      ? 'The sender does not resolve to a client.'
      : `It looks like ${clientName ?? payment.client}.`,
  )

  const nearest = payment.candidates[0]
  if (nearest === undefined) {
    parts.push('Nothing open matches it, so it needs placing by hand.')
  } else {
    parts.push(
      `The nearest open invoice is ${nearest.invoice_ref}, with ${money(nearest.outstanding)} outstanding` +
        `${gapWords(nearest, payment.amount, money)}.`,
    )
    /*
     * A candidate in another currency is worth saying out loud rather than leaving to
     * be discovered in the dialog: `payments_match` allocates in the **credit's** own
     * currency, so `fill` cannot preset an amount for it and a person has to decide
     * what the conversion was.
     */
    if (nearest.outstanding.currency !== payment.amount.currency) {
      parts.push(
        `It is in ${nearest.outstanding.currency} and the credit is in ${payment.amount.currency}, so nothing can be allocated to it without a figure somebody works out.`,
      )
    }
  }

  if (payment.note !== '') parts.push(payment.note)

  return parts.join(' ')
}

/**
 * " — $7.00 short of it", from `MatchCandidateSchema.difference`.
 *
 * `difference` is the credit's amount minus that invoice's outstanding, in minor
 * units, and it is the whole of `mockups/studio/money.html`'s "Amount is $7.00 short
 * of FES-2026-051". Nothing here subtracts anything: the field is the answer and
 * `money.money()` turns it back into a `Money` to be formatted.
 *
 * Only stated when both are in the **credit's** currency — a `difference` across two
 * currencies is a number the read model computed against a rate this page does not
 * know, and printing it beside a EUR figure would read as a EUR amount.
 */
export function gapWords(
  candidate: Candidate,
  credit: Money,
  money: (value: Money) => string,
): string {
  if (candidate.difference === 0) return ' — exactly'
  if (candidate.outstanding.currency !== credit.currency) return ''
  /* The sign is the direction and the magnitude is the figure; both come off the one
     field, so this negates rather than computing anything. */
  const minor = candidate.difference < 0 ? -candidate.difference : candidate.difference
  const size = money(makeMoney(minor, credit.currency))
  return candidate.difference < 0 ? ` — ${size} short of it` : ` — ${size} more than it`
}

/** `wise:8841b2` → `step_id 8841b2`, which is what the mockup prints. */
export function stepIdOf(externalId: string): string {
  const [, rest] = externalId.split(':')
  return rest === undefined ? externalId : `step_id ${rest}`
}

/* ------------------------------------------------------------ the candidates */

function CandidateList({
  candidates,
  pending,
  failed,
}: {
  readonly candidates: readonly Candidate[]
  readonly pending: boolean
  readonly failed: boolean
}) {
  if (pending) return <Empty>Reading what is still open…</Empty>
  if (failed) return <Notice need>We could not re-read the open invoices.</Notice>
  if (candidates.length === 0) return <Empty>Nothing open matches it.</Empty>

  return (
    <Table>
      <THead>
        <Tr>
          <Th scope="col">Invoice</Th>
          <Th scope="col">Due</Th>
          <Th scope="col" right>
            Outstanding
          </Th>
        </Tr>
      </THead>
      <TBody>
        {candidates.map((one) => (
          <Tr key={one.invoice_ref}>
            <Td>
              <Ref value={one.invoice_ref} nowrap />
            </Td>
            {/* A `DateOnly`, so `DayText` and not `DateText` — `CLAUDE.md` §The client
                portal: it is the only place a day becomes a rendering, read at noon
                UTC so no viewer's zone carries it into the day before. */}
            <Td date>{one.due_on === null ? <Muted>—</Muted> : <DayText value={one.due_on} />}</Td>
            <Td right num>
              <Num value={one.outstanding} />
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  )
}

/* ------------------------------------------------------------- the allocation */

/** One row of the editor: an invoice, and what a person has typed against it. */
type Draft = { readonly candidate: Candidate; readonly major: string }

/**
 * The preset behind a "Match to…" or a "Split across…" button.
 *
 * Fills each candidate in order with the smaller of what is left of the credit and
 * what that invoice still owes, which is the only allocation that is right for both
 * a credit that covers everything and one that does not. Whatever is left over is
 * the remainder the dialog then prints.
 */
export function fill(credit: Money, candidates: readonly Candidate[]): readonly Draft[] {
  let left = credit
  const drafts: Draft[] = []

  for (const candidate of candidates) {
    /*
     * The credit's own currency is what `payments_match` allocates in — its input
     * says "in the credit's currency" — so an invoice carrying a different one gets
     * no preset amount and has to be typed. `subtract` refuses a mixture anyway, and
     * a silent zero would be a body somebody sent without noticing.
     */
    if (candidate.outstanding.currency !== credit.currency) {
      drafts.push({ candidate, major: '' })
      continue
    }
    const take = candidate.outstanding.amount < left.amount ? candidate.outstanding : left
    drafts.push({ candidate, major: isZero(take) ? '' : String(toMajorUnits(take)) })
    left = subtract(left, take)
  }

  return drafts
}

/**
 * The editor's state: one major-unit box per invoice reference.
 *
 * **Keyed by reference rather than by index** — react-hook-form's `FieldPath` over an
 * array is a template literal in `number`, so an indexed name has to be built by
 * interpolating one, which is a rule this repository bans and `ActionField` only
 * escapes because its own values type is `Record<string, unknown>`. A reference is a
 * string, is unique among a credit's candidates, and contains no dot to be read as a
 * path separator. It is also what the row is about.
 */
const AllocationFormSchema = z.record(z.string(), z.string())

type AllocationForm = z.infer<typeof AllocationFormSchema>

/**
 * The one key in that record that is not an invoice reference.
 *
 * `payments_match$` requires a `note` — "Why it was placed this way… It stays on the
 * record", three characters at least — and the form is keyed by reference, so the
 * note needs a key no reference can be. `FES-YYYY-NNN` is the only shape a reference
 * takes (`InvoiceRefSchema`), so a leading underscore is enough and is checked by the
 * pattern rather than by hope.
 */
const NOTE_FIELD = '_note'

/** How long a note has to be. `payments_match$`'s own `min(3)`. */
const NOTE_MIN = 3

/**
 * What the note says before anybody edits it.
 *
 * A required field with no default is a field somebody types "x" into. This says why
 * the credit is being placed by a person at all, which is what `payments_match$`'s
 * `note` is for — "Why it was placed this way".
 *
 * **It names no amount, and that is the fix rather than the omission.** It used to
 * open with "…€1,842.60 held on account", computed **once** from the remainder the
 * dialog had at the instant it opened, and `defaultValues` is applied once: allocating
 * €890.00 and €452.60 of that credit left the confirm button correctly reading
 * "…holding €500.00 on account" over a note still saying €1,842.60. What committed
 * was one `audit_log` row carrying both — `"note": "…€1,842.60 held on account."`
 * beside `"on_account": 50000` — and `audit_log` has **no UPDATE grant**, so the row
 * that contradicts itself is the permanent one (`CLAUDE.md` §Records are appended).
 *
 * Recomputing it on every keystroke is the other repair and is worse: `defaultValues`
 * seeds a box a person then owns, and a sentence that rewrote itself under them would
 * throw away what they typed. So the figure goes, and nothing is lost by it — the
 * amount held is `hold_on_account` on the same audit row, printed in the dialog's own
 * total line and stated again on the confirm button. The note is the *why*; the
 * fields are the *what*, and only one of them can be wrong.
 *
 * The short-credit variant keeps its wording because it is read off
 * `payment.candidates`, which nothing in this dialog changes.
 */
export function defaultNote(payment: UnmatchedPaymentView): string {
  const short = payment.candidates.some((one) => one.difference < 0)
  return short
    ? 'Placed by hand from the review queue — the credit is short of the invoice, which is usually the sender’s fee.'
    : 'Placed by hand from the review queue.'
}

/** One line of the body `payments_match$` is about to be sent: an invoice and its share. */
export type Allocation = { readonly invoice: string; readonly amount: Money }

/**
 * What the confirm button says — **the amount it is about to commit**, and to what.
 *
 * `CLAUDE.md` §Money: "Every `$` action states the amount it commits." It stated the
 * whole credit instead, on every allocation, which is the same sentence only when the
 * credit happens to be spent on one invoice. Session 24 (R3-03): allocating $1,000 of
 * a $2,993.00 credit left a live button reading `Match $2,993.00 against FES-2026-051`
 * over a body that would put $1,000.00 on that invoice and $1,993.00 on account — and
 * the two-invoice split had the same defect in the other direction, naming one invoice
 * for a body that touched two.
 *
 * So the three facts the body carries are the three the label carries: the total being
 * allocated, every invoice it is allocated to, and the remainder going on account.
 * Nothing here computes one of them — `allocated` and `held` are the dialog's own
 * `sum`/`subtract` over the typed boxes, which is where `CLAUDE.md` §Money says the
 * arithmetic lives.
 *
 * A zero allocation still names the row the dialog was opened on rather than "nothing":
 * the button is disabled and the notice under it says why, and a label that lost its
 * reference would leave the disabled control describing no invoice at all.
 */
export function matchLabel(
  allocations: readonly Allocation[],
  allocated: Money,
  held: Money,
  money: (value: Money) => string,
): string {
  const committed = allocations.filter((one) => !isZero(one.amount))
  const named = (committed.length === 0 ? allocations.slice(0, 1) : committed).map(
    (one) => one.invoice,
  )
  const where =
    named.length === 0
      ? 'against nothing'
      : named.length === 1
        ? `against ${named[0] ?? ''}`
        : `across ${formatList(named)}`
  /* Only a **positive** remainder is a hold. A negative one is the over-allocation the
     notice already refuses in words, and `hold_on_account` is never sent for it. */
  const rest = isZero(held) || isNegative(held) ? '' : `, holding ${money(held)} on account`
  return `Match ${money(allocated)} ${where}${rest}`
}

/**
 * *Allocate manually* — the same editor over **every** invoice this credit could be
 * matched to, read when the button is pressed.
 *
 * It is a separate component so the read is not made until somebody asks for it: a
 * queue of four credits would otherwise open four `payments_list_candidates` calls on
 * every render of the money page.
 *
 * The three answers are three states and each one says what it is. A credit with
 * nothing allocatable is the honest end of the road — a USD credit with only EUR
 * invoices open is one `payments_match` refuses in as many words — and the dialog says
 * so and names the two moves that are left, rather than opening an editor with no rows
 * in it and a confirm button that can never light up.
 */
function ManualDialog({
  payment,
  onClose,
}: {
  readonly payment: UnmatchedPaymentView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const open = useStudioRead(
    'payments_list_candidates',
    { payment: payment.id, scope: 'allocatable' },
    { inline: true },
  )
  const rows = open.data?.candidates

  if (rows !== undefined && rows.length > 0) {
    return (
      <MatchDialog
        payment={payment}
        drafts={rows.map((candidate) => ({ candidate, major: '' }))}
        onClose={onClose}
      />
    )
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Place a credit by hand"
      confirmLabel={`Reading what ${format.money(payment.amount)} could settle…`}
      busy
      onConfirm={() => {
        /* Nothing to confirm: the button is disabled in all three of these states. */
      }}
    >
      <Stack>
        {open.isPending ? (
          <Empty>Reading every open invoice this could settle…</Empty>
        ) : open.isError ? (
          <Notice need role="alert">
            {messageOf(open.error)}
          </Notice>
        ) : (
          <Notice need role="alert">
            {`Nothing open can take this. payments_match allocates in the credit’s own currency, so an invoice in another one is not a choice here — every open invoice in ${payment.amount.currency} is already settled or already spoken for. Park it with a note, or record the money against an invoice in its own currency once one exists.`}
          </Notice>
        )}
      </Stack>
    </ConfirmDialog>
  )
}

function MatchDialog({
  payment,
  drafts,
  onClose,
}: {
  readonly payment: UnmatchedPaymentView
  readonly drafts: readonly Draft[]
  readonly onClose: () => void
}) {
  const format = useFormat()
  const match = useStudioAction('payments_match')
  const review = reviewOf(match.data)

  const form = useZodForm<AllocationForm, AllocationForm>(AllocationFormSchema, {
    defaultValues: {
      ...Object.fromEntries(drafts.map((one) => [one.candidate.invoice_ref, one.major])),
      [NOTE_FIELD]: defaultNote(payment),
    },
  })
  const typed = form.watch()

  const allocations = drafts.map((draft) => ({
    invoice: draft.candidate.invoice_ref,
    amount: amountOf(typed[draft.candidate.invoice_ref] ?? '', payment.amount.currency),
  }))
  const note = (typed[NOTE_FIELD] ?? '').trim()
  const allocated = sum(
    allocations.map((one) => one.amount),
    payment.amount.currency,
  )
  const held = subtract(payment.amount, allocated)
  const overspent = isNegative(held)
  const nothing = isZero(allocated)
  /*
   * A **negative** allocation, typed into one box, is refused on its own.
   *
   * `MoneySchema`'s minimum is `-9007199254740991` — a negative amount is a legal
   * `Money` because a discount line is one — so nothing downstream would have caught
   * "−500 to this invoice, 3,493 to that one". The two halves still add up to the
   * credit and `overspent` is false, so the body would have gone out and taken money
   * *off* an invoice nobody meant to touch.
   */
  const backwards = allocations.some((one) => isNegative(one.amount))
  /* `payments_match$`'s `note` is **required** — a match with no note is a 400. */
  const unexplained = note.length < NOTE_MIN
  const refused = overspent || nothing || backwards || unexplained

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Match a credit to one or more invoices"
      confirmLabel={matchLabel(allocations, allocated, held, format.money)}
      busy={match.isPending || refused}
      onConfirm={() => {
        if (refused) return
        match.mutate(
          {
            payment: payment.id,
            allocations: allocations.filter((one) => !isZero(one.amount)),
            note,
            ...(isZero(held) ? {} : { hold_on_account: held }),
          },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`This credit is ${format.money(payment.amount)}. Every unit of it has to go somewhere: what is not allocated below is held on account, and a body that does not add up is refused.`}
        </Prose>

        <Table>
          <THead>
            <Tr>
              <Th scope="col">Invoice</Th>
              <Th scope="col" right>
                Outstanding
              </Th>
              <Th scope="col" right>
                Allocate
              </Th>
            </Tr>
          </THead>
          <TBody>
            {drafts.map((draft) => (
              <Tr key={draft.candidate.invoice_ref}>
                <Td>
                  <Ref value={draft.candidate.invoice_ref} nowrap />
                </Td>
                <Td right num>
                  <Num value={draft.candidate.outstanding} />
                </Td>
                <Td right>
                  <Input
                    control={form.control}
                    name={draft.candidate.invoice_ref}
                    label={`Allocated to ${draft.candidate.invoice_ref}`}
                    hint={payment.amount.currency}
                  />
                </Td>
              </Tr>
            ))}
            <Tr variant="total">
              <Td colSpan={2}>{overspent ? 'Over the credit by' : 'Held on account'}</Td>
              <Td right num>
                <Num
                  value={overspent ? subtract(allocated, payment.amount) : held}
                  bad={overspent}
                />
              </Td>
            </Tr>
          </TBody>
        </Table>

        {/* Required by the action, and it stays on the record — so it is a field
            with a sentence already in it rather than an empty box. */}
        <Input
          control={form.control}
          name={NOTE_FIELD}
          label="Why it was placed this way"
          hint="It stays on the payment. “The sender’s FX fee, written off.”"
        />

        {backwards ? (
          <Notice need role="alert">
            An allocation cannot be negative. Money comes off an invoice through a refund, not
            through a match.
          </Notice>
        ) : overspent ? (
          <Notice need role="alert">
            The allocations come to more than the credit. Take the difference off one of them.
          </Notice>
        ) : nothing ? (
          <Notice need role="alert">
            Nothing is allocated yet. To set the whole credit aside instead, park it.
          </Notice>
        ) : unexplained ? (
          <Notice need role="alert">
            Say why it is being placed this way. The note stays on the payment.
          </Notice>
        ) : null}
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {match.isError ? (
          <Notice need role="alert">
            {messageOf(match.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------------ parking */

const ParkFormSchema = z.object({
  note: z.string().trim().min(3, 'Say what was tried.').max(500),
})

type ParkForm = z.infer<typeof ParkFormSchema>

function ParkDialog({
  payment,
  onClose,
}: {
  readonly payment: UnmatchedPaymentView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const park = useStudioAction('payments_park')
  const review = reviewOf(park.data)

  const form = useZodForm<ParkForm, ParkForm>(ParkFormSchema, { defaultValues: { note: '' } })
  const note = form.watch('note')

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Set an unidentified credit aside"
      confirmLabel={`Park ${format.money(payment.amount)} against ${stepIdOf(payment.external_id)}`}
      busy={park.isPending || note.trim().length < 3}
      onConfirm={() => {
        if (note.trim().length < 3) return
        park.mutate(
          { payment: payment.id, note: note.trim() },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`Parking leaves the ${format.money(payment.amount)} where it is and takes it out of this queue. It is still the studio’s money and it still belongs to nobody; the note is what the next person reads.`}
        </Prose>
        <Input
          control={form.control}
          name="note"
          label="What was tried"
          hint="“asked them on the 14th, no answer”"
        />
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {park.isError ? (
          <Notice need role="alert">
            {messageOf(park.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------------- income */

const IncomeFormSchema = z.object({
  client: z.string().min(1, 'Choose the account it is income of.'),
  note: z.string().trim().min(3, 'Say why it is income.').max(500),
})

type IncomeForm = z.infer<typeof IncomeFormSchema>

/**
 * "Book as income" — `payments_book_income$`, session 47.
 *
 * For money that is an account's and no invoice's: Far East Novels is a Patreon the
 * studio owns, paid out through Payoneer, with no invoices and never any, and every
 * payout used to sit here with nothing to do but park it. The whole credit goes, which
 * is why there is no amount to type — the tool states it and refuses anything else.
 * The account defaults to the one the sender already resolves to, when it does.
 *
 * `tasks` is refreshed too: the booking closes the credit's "Match payment" task, and a
 * `payments_*` call refreshes `billing`, `analytics` and `clients` and nothing else.
 */
function IncomeDialog({
  payment,
  onClose,
}: {
  readonly payment: UnmatchedPaymentView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const book = useStudioAction('payments_book_income', { alsoInvalidates: ['tasks'] })
  const review = reviewOf(book.data)
  const clients = useStudioRead(
    'clients_list',
    { include_archived: false, limit: 100 },
    { inline: true },
  )

  const form = useZodForm<IncomeForm, IncomeForm>(IncomeFormSchema, {
    defaultValues: { client: payment.client ?? '', note: '' },
  })
  const client = form.watch('client')
  const note = form.watch('note')
  const ready = client !== '' && note.trim().length >= 3

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Book a credit as an account’s income"
      confirmLabel={`Book ${format.money(payment.amount)} as income`}
      busy={book.isPending || !ready}
      onConfirm={() => {
        if (!ready) return
        book.mutate(
          { payment: payment.id, client, amount: payment.amount, note: note.trim() },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`Income is money that is the account’s and no invoice’s — a platform payout to an account the studio owns. All ${format.money(payment.amount)} goes to it, counts as collected, and this credit’s Match payment task is closed.`}
        </Prose>
        <Select
          control={form.control}
          name="client"
          label="Whose income"
          options={(clients.data?.clients ?? []).map((row) => ({
            value: row.slug,
            label: row.name,
          }))}
          disabled={clients.isPending}
          placeholder="Choose a client"
        />
        <Input
          control={form.control}
          name="note"
          label="Why it is income"
          hint="“Patreon payout via Payoneer”"
        />
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {book.isError ? (
          <Notice need role="alert">
            {messageOf(book.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}
